import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import {
  isSeat,
  isPetFriendlySeat,
  getSeatCategory,
  isBlockedByAdaGroup,
  isSeatOccupied,
} from 'utils/purchase/seats';
import {
  isHallwaySeat as isHallwaySeatUtil,
  hasHallwaySeatInRow as hasHallwaySeatInRowUtil,
  hasHallwaySeatInDeck as hasHallwaySeatInDeckUtil,
  adjustRowForHallwaySeat as adjustRowForHallwaySeatUtil,
  computeHallwayMeta,
} from 'utils/purchase/hallwaySeat';
import { Box, Gap, Icon, MessageBox, Spacing, Text } from '@reservamos/elements';
import BadgeNew from 'ui/atoms/BadgeNew';
import 'shared-styles/purchase/BusDiagram';
import 'styles/components/purchase/BusDiagram';
import i18n from 'i18next';
import NewSeat from 'ui/atoms/NewSeat';
import { isSeatsOnResultActivated } from 'utils/seats';
import { buildZoneMap, getSeatZone } from 'utils/seatPricing';
import PetBadge from '../../images/badges/pet.svg';
import AdjacentIcon from '../../ui/atoms/AdjacentIcon';

const propTypes = {
  layout: PropTypes.arrayOf(
    PropTypes.arrayOf(
      PropTypes.arrayOf(
        PropTypes.shape({
          category: PropTypes.string.isRequired,
          number: PropTypes.string,
          occupied: PropTypes.bool,
          intermediateSeat: PropTypes.string,
        }),
      ),
    ),
  ),
  diagramType: PropTypes.string,
  selectedSeats: PropTypes.array,
  lockedSeats: PropTypes.array,
  changeSeat: PropTypes.func,
  disabled: PropTypes.bool.isRequired,
  showPetFriendly: PropTypes.bool,
  petFriendlySoldOut: PropTypes.bool,
  showAdjacentIcon: PropTypes.bool,
  id: PropTypes.string,
  isResultsLayout: PropTypes.bool,
  switchDeck: PropTypes.func,
  selectedDeck: PropTypes.number,
  verticalSeats: PropTypes.bool,
  firstFloorReclination: PropTypes.string,
  secondFloorReclination: PropTypes.string,
  showUniformLayout: PropTypes.bool,
  enableHallwaySeatAlignment: PropTypes.bool,
  adjacentToggleActive: PropTypes.bool,
  seatPriceZones: PropTypes.array,
};

/**
 * Represents a bus diagram component.
 */
export class BusDiagram extends Component {
  constructor(props) {
    super(props);
    this.selectDeck = this.selectDeck.bind(this);
  }

  changeSeat(busSpace) {
    const { changeSeat, lockedSeats } = this.props;
    const { number } = busSpace;

    if (isSeatOccupied(busSpace) && !lockedSeats.includes(number)) return;
    changeSeat(busSpace);
  }

  selectDeck({ selectedDeck }) {
    return () => {
      const { switchDeck } = this.props;
      switchDeck({ selectedDeck });
    };
  }

  /**
   * Determines if a deck contains hallway seats.
   * @param {Array} deck - The deck to check for hallway seats.
   * @returns {boolean} True if the deck contains hallway seats, false otherwise.
   */
  deckContainsHallwaySeat(deck) {
    return (this.hallwayMeta && this.hallwayMeta.deckHasHallway) || hasHallwaySeatInDeckUtil(deck);
  }

  // Create an adjusted row by inserting extra middle elements before and after index 3
  createAdjustedRowForHallwaySeat(deck, row, rIndex, originalRow) {
    const { enableHallwaySeatAlignment, diagramType } = this.props;
    return adjustRowForHallwaySeatUtil(deck, row, rIndex, originalRow, {
      enabled: enableHallwaySeatAlignment,
      diagramType,
    });
  }

  // Adjust a row when the deck contains hallway_seat but this row doesn't
  adjustRowForHallwaySeat(deck, row, rIndex, originalRow) {
    const { showUniformLayout, diagramType, enableHallwaySeatAlignment } = this.props;

    if (!enableHallwaySeatAlignment || showUniformLayout || diagramType === 'van') {
      return originalRow;
    }

    const deckHasHallway = this.deckContainsHallwaySeat(deck);
    if (!deckHasHallway) return originalRow;

    const rowHasHallway =
      (this.hallwayMeta &&
        this.hallwayMeta.rowsHasHallway &&
        this.hallwayMeta.rowsHasHallway[rIndex]) ||
      hasHallwaySeatInRowUtil(row);
    if (rowHasHallway) return originalRow;

    return this.createAdjustedRowForHallwaySeat(deck, row, rIndex, originalRow);
  }

  renderBusSpace(deck, busSpace, rIndex, index) {
    const {
      lockedSeats,
      selectedSeats,
      diagramType,
      showPetFriendly,
      showAdjacentIcon,
      isResultsLayout,
      verticalSeats,
      showUniformLayout,
      enableHallwaySeatAlignment,
    } = this.props;

    const isVerticalLayout = verticalSeats && !isResultsLayout;
    const { number, seatLevel } = busSpace;
    const occupied = isSeatOccupied(busSpace);
    const zoneMeta = getSeatZone(busSpace, this.zoneMap || {});
    const categoryOrType = getSeatCategory(busSpace);
    const deckContainsHallwaySeat = this.deckContainsHallwaySeat(deck);
    let itemClass = categoryOrType.replace(/_/gi, '-');
    // TODO: Patch to remove pet-seat if showPetFriendly is false. This is a temporary solution. Issue: https://reservamossaas.atlassian.net/browse/ENT-809
    if (!showPetFriendly && isPetFriendlySeat(categoryOrType)) {
      itemClass = 'seat';
    }

    /**
     * Determines the CSS class for a bus space.
     * @returns {string} The CSS class name.
     */
    const busSpaceClass = () => {
      const isInLastRow = deck.length === rIndex + 1;
      const isVanAisle = index === 5 && diagramType === 'van' && !isInLastRow;
      const isBusAisle = index === 3 && diagramType !== 'van';

      if (showUniformLayout) {
        return 'seats-layout-item';
      }

      // If this is a hallway_seat, it should be treated as a middle item
      if (isHallwaySeatUtil(busSpace)) {
        return 'seats-layout-item-middle';
      }

      if ([0, 2, 4, 6].includes(index)) {
        return 'seats-layout-item';
      }
      if (isVanAisle || isBusAisle) {
        // When hallway_seat exists in the deck, the central aisle (index 3) should be middle
        if (enableHallwaySeatAlignment && diagramType !== 'van' && deckContainsHallwaySeat) {
          return 'seats-layout-item-middle';
        }
        return 'seats-layout-aisle';
      }

      return 'seats-layout-item-middle';
    };

    /**
     * Determines the CSS class for a seat based on its selection and occupation status.
     * @returns {string} The CSS class for the seat.
     */
    const seatClass = () => {
      const seat = selectedSeats.find(
        (seat) =>
          String(seat.number) === String(number) &&
          (!seatLevel || String(seat.seatLevel) === String(seatLevel)),
      );

      if (seat) {
        if (seat.isPickedAsAdjacent) {
          itemClass = 'adjacent-seat';
        }
        return `${itemClass}-selected`;
      }

      if (occupied) {
        return itemClass + (lockedSeats.includes(number) ? '-available' : '-occupied');
      }

      return `${itemClass}-available`;
    };

    /**
     * Checks if the seat should be disabled due to adjacent toggle being active.
     * @returns {boolean} True if the seat should be disabled due to adjacent toggle, false otherwise.
     */
    const isDisabledByAdjacentToggle = () => {
      const { adjacentToggleActive } = this.props;
      // If the adjacent toggle is active and this seat has no adjacent seats, it should be disabled
      return adjacentToggleActive && busSpace.adjacentSeats === null && isSeat(categoryOrType);
    };

    /**
     * Determines the seat type based on its selection, occupation status, and adjacent toggle state.
     * @returns {string} The seat type ('selected', 'adjacent', 'occupied', 'available', or 'disabled').
     */
    const getSeatType = () => {
      const seat = selectedSeats.find(
        (seat) =>
          String(seat.number) === String(number) &&
          (!seatLevel || String(seat.seatLevel) === String(seatLevel)),
      );

      if (seat) {
        if (seat.isPickedAsAdjacent) {
          return 'adjacent';
        }
        return 'selected';
      }

      if (occupied) {
        if (lockedSeats.includes(number)) {
          return 'available';
        }
        return 'occupied';
      }

      // If the adjacent seat toggle is active and this seat is not adjacent, show it as disabled
      if (isDisabledByAdjacentToggle()) {
        return 'disabled';
      }

      if (isBlockedByAdaGroup(selectedSeats, number, seatLevel)) {
        return 'disabled';
      }

      return 'available';
    };

    /**
     * Determines if a seat is disabled based on its occupied status and the locked seats.
     * If the seat is occupied and not locked, it is disabled.
     * When the user tries to select a seat that is already taken but is locked by himself, it is enabled.
     * Also, if the adjacent toggle is active and the seat is not adjacent, it is disabled.
     * @returns {boolean} True if the seat is disabled, false otherwise.
     */
    const isSeatDisabled = () => {
      // If the seat is occupied and not in the locked seats, it is disabled
      if (occupied && !lockedSeats.includes(number)) {
        return true;
      }

      if (isBlockedByAdaGroup(selectedSeats, number, seatLevel)) return true;

      // Check if the seat should be disabled due to adjacent toggle
      return isDisabledByAdjacentToggle();
    };

    /**
     * Get the seat icon based on the category and showPetFriendly flag.
     * @returns {string} The seat icon.
     */

    let busSpaceType = <i className={itemClass} />;

    // Render hallway_seat before checking generic isSeat(category)
    if (isSeat(categoryOrType)) {
      busSpaceType = (
        <NewSeat
          number={number}
          type={getSeatType()}
          zone={zoneMeta.zoneNumber}
          hasTv={categoryOrType === 'seat_tv'}
          onClick={() => this.changeSeat(busSpace)}
          newDesign={isResultsLayout}
          showPetFriendly={showPetFriendly}
          category={categoryOrType}
          seatClass={seatClass()}
          disabled={isSeatDisabled()}
          topTvOnDesktop={isVerticalLayout}
        />
      );
    }

    // TODO: Move these conditions to a Factory that returns the view that should be shown based on the category
    if (categoryOrType === 'hallway_adjacent' && showAdjacentIcon) {
      busSpaceType = <AdjacentIcon isVertical={isVerticalLayout} />;
    }

    // Identify seats on the wrapper that already exists, so both render paths (the
    // legacy button and the Elements NewSeat) are covered without adding a node or
    // touching layout. Availability is part of the id because the seat itself exposes
    // nothing an accessibility-tree consumer can use to tell free from occupied.
    // Availability, NOT `isSeatDisabled()`: that also returns true for the adjacent-seat
    // toggle, which is interaction state — a free seat would then be labelled occupied.
    // A seat the user locked themselves is still theirs to pick, so it counts as available.
    const isSeatTaken = Boolean(occupied) && !lockedSeats.includes(number);
    const seatDomId = isSeat(categoryOrType)
      ? `seats-${isSeatTaken ? 'occupied' : 'available'}-${number}`
      : undefined;

    return (
      <div key={`bd-bs-${rIndex}-${index}`} id={seatDomId} className={busSpaceClass()}>
        {busSpaceType}
      </div>
    );
  }

  renderRow(deck, row, index) {
    const originalRow = row.map((busSpace, bsIndex) => {
      return this.renderBusSpace(deck, busSpace, index, bsIndex);
    });
    const adjustedRow = this.adjustRowForHallwaySeat(deck, row, index, originalRow);
    return (
      <div key={`bd-row-${index}`} className="seats-layout-row">
        {adjustedRow}
      </div>
    );
  }

  renderDeck(deck, index, roundedSeatsAmount) {
    const {
      diagramType,
      disabled,
      petFriendlySoldOut,
      isResultsLayout,
      verticalSeats,
      seatPriceZones,
    } = this.props;
    const resultsSeatsAb = isSeatsOnResultActivated();
    this.zoneMap = buildZoneMap({ seatPriceZones });

    const isVerticalLayout = verticalSeats && !isResultsLayout;
    const disabledClass = disabled ? 'vehicle-container-disabled' : '';
    const verticalLayout = isVerticalLayout ? 'vehicle-container-vertical' : '';
    const newLayout = isResultsLayout ? 'new-container' : '';
    const vehicleLayoutConfig = `${verticalLayout} ${newLayout} ${diagramType} ${disabledClass}`;
    const rowsCount = deck.length;
    const moreThanTwentyRows = rowsCount > 14;
    const largeSeatNumber = roundedSeatsAmount > 50;
    const additionalClass = moreThanTwentyRows ? 'hundred' : '';

    const petSoldOutMessage = petFriendlySoldOut && (
      <Box borderRadius="S" hasShadow paddingHorizontal="S" paddingVertical="S">
        <Spacing alignItems="center" size="S">
          <Icon type="Info" size="S" />
          <Text>{i18n.t('seats:pets_soldout')}</Text>
        </Spacing>
      </Box>
    );

    return (
      <Spacing vertical>
        {petSoldOutMessage}

        {largeSeatNumber && (
          <MessageBox borderColor="info">
            <Gap alignItems="center">
              <BadgeNew />
              <Text size="S">{i18n.t('seats:many_seats', { count: roundedSeatsAmount })}</Text>
            </Gap>
          </MessageBox>
        )}

        <Spacing vertical size="S">
          <div className={`vehicle-container ${vehicleLayoutConfig}`}>
            {resultsSeatsAb && isResultsLayout && (
              <div className="vehicle-front">
                <div className="wheel-front">
                  <i className="wheel-front-icon" />
                </div>
              </div>
            )}

            {!isResultsLayout && <i className={`${diagramType}-front`} />}

            <div key={`bd-deck-${index}`} className={`seats-layout ${additionalClass}`}>
              {(() => {
                // Precompute hallway metadata for the current deck to avoid repeated scans
                const { enableHallwaySeatAlignment } = this.props;
                this.hallwayMeta = computeHallwayMeta(deck, {
                  enabled: enableHallwaySeatAlignment,
                });
                return deck.map((row, rIndex) => this.renderRow(deck, row, rIndex));
              })()}
            </div>

            {!isResultsLayout && <i className={`${diagramType}-back`} />}
          </div>
        </Spacing>
      </Spacing>
    );
  }

  renderDiagram(layout) {
    const firstDeck = layout[0];
    const secondDeck = layout[1];
    const { showPetFriendly, id, selectedDeck, firstFloorReclination, secondFloorReclination } =
      this.props;
    const resultsSeatsAb = isSeatsOnResultActivated();
    const flattenLayout = layout.flat(2).filter((element) => isSeat(getSeatCategory(element)));
    const roundedSeatsAmount = Math.floor(flattenLayout.length / 10) * 10;
    if (secondDeck) {
      return (
        <div className="tabs">
          {!resultsSeatsAb && (
            <div className="tabs-top">
              <ul className="tabs-list">
                <li className="tab tabs-item tabs-2" onClick={this.selectDeck({ selectedDeck: 1 })}>
                  <a
                    href={`#secondDeck-${id}`}
                    className={`tabs-item-content ${selectedDeck === 1 ? 'active' : ''}`}
                  >
                    {`${i18n.t('seats:2nd_floor')}${
                      secondFloorReclination ? ` - ${secondFloorReclination}` : ''
                    }`}
                  </a>
                </li>
                <li className="tab tabs-item tabs-2" onClick={this.selectDeck({ selectedDeck: 0 })}>
                  <a
                    href={`#firstDeck-${id}`}
                    className={`tabs-item-content ${selectedDeck === 0 ? 'active' : ''}`}
                  >
                    <Gap alignItems="center">
                      {`${i18n.t('seats:1st_floor')}${
                        firstFloorReclination ? ` - ${firstFloorReclination}` : ''
                      }`}
                      {showPetFriendly && <Icon type={PetBadge} size="M" mobileSize="S" />}
                    </Gap>
                  </a>
                </li>
              </ul>
            </div>
          )}

          <div>
            {this.renderDeck(selectedDeck === 0 ? firstDeck : secondDeck, 0, roundedSeatsAmount)}
          </div>
        </div>
      );
    }

    return this.renderDeck(firstDeck, 0, roundedSeatsAmount);
  }

  render() {
    const { layout } = this.props;

    return <div className="animated fade-in">{this.renderDiagram(layout)}</div>;
  }
}

BusDiagram.propTypes = propTypes;
BusDiagram.defaultProps = {
  layout: [
    [
      [
        { category: 'seat_tv', number: '1', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '2', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '3', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '4', occupied: false },
      ],
      [
        { category: 'seat', number: '5', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '6', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '7', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '8', occupied: false },
      ],
      [
        { category: 'seat', number: '9', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '10', occupied: false },
        { category: 'hallway' },
        { category: 'seat_tv', number: '11', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '12', occupied: false },
      ],
      [
        { category: 'seat_tv', number: '13', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '14', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '15', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '16', occupied: false },
      ],
      [
        { category: 'seat', number: '17', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '18', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '19', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '20', occupied: false },
      ],
      [
        { category: 'seat', number: '21', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '22', occupied: false },
        { category: 'hallway' },
        { category: 'seat_tv', number: '23', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '24', occupied: false },
      ],
      [
        { category: 'seat', number: '25', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '26', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '27', occupied: false },
        { category: 'hallway' },
        { category: 'seat', number: '28', occupied: false },
      ],
      [
        { category: 'seat', number: '29', occupied: false },
        { category: 'hallway' },
        { category: 'hallway' },
        { category: 'hallway' },
        { category: 'hallway' },
        { category: 'hallway' },
        { category: 'seat', number: '30', occupied: false },
      ],
    ],
  ],
  diagramType: 'bus',
  selectedSeats: [],
  lockedSeats: [],
  changeSeat: null,
  enableHallwaySeatAlignment: true,
  adjacentToggleActive: false,
  seatPriceZones: [],
};

const mapStateToProps = (state) => ({
  copies: state.whitelabelConfig.copies,
  enableHallwaySeatAlignment:
    state.whitelabelConfig?.features?.HALLWAY_SEAT_ALIGNMENT_ENABLED !== false,
});

export default connect(mapStateToProps)(BusDiagram);
